import React from 'react'
import { CARRIERS, formatNumber } from './carriers.js'
import { assignCarrier, parsePackageDetails } from '../../utils/carrierAssignment'
import DealLink from '../shared/DealLink.jsx'

export default function ShipmentCard({ shipment, onClick, isSelected, batchMode, isChecked, onToggleSelect }) {
  const autoAssigned = assignCarrier(shipment)
  // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
  const hubspotCarrier = shipment.carrier?.toLowerCase()
  const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier]
    ? hubspotCarrier
    : autoAssigned.carrierId
  const carrier = CARRIERS[carrierId]
  const pkg = parsePackageDetails(shipment.package_details)
  const totalWeight = pkg?.totalWeight || 0
  const boxCount = pkg?.boxes?.length || 1
  const isDropOff = shipment.send_type === 'dropoff'
  // "Deliver in person" (spec_20260805): no carrier, no label — the card
  // shows the visit slot instead of a carrier badge.
  const isWarehouse = shipment.send_type === 'warehouse_dropoff'
  const hasPrinter = shipment.has_printer?.toLowerCase() === 'yes'
  // Just "Anytime": the card cannot know the day's real open window, and
  // "9am–5pm" would lie on a trimmed day (spec decision #4).
  const slotText = shipment.dropoff_slot_start
    ? `${shipment.dropoff_slot_start.slice(0, 5)}–${String(Number(shipment.dropoff_slot_start.slice(0, 2)) + 1).padStart(2, '0')}:00`
    : 'Anytime'

  const handleClick = () => {
    if (batchMode) {
      onToggleSelect?.(shipment.id)
    } else {
      onClick()
    }
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: isChecked ? 'rgba(77, 20, 140, 0.08)' : isSelected ? 'rgba(59, 178, 115, 0.1)' : 'white',
        border: isChecked ? '2px solid #4D148C' : isSelected ? '2px solid var(--green-onions)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 'var(--space-3)',
        marginBottom: 'var(--space-2)',
        cursor: 'pointer',
        transition: 'all 0.15s'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 2 }}>
        {batchMode && (
          <input
            type="checkbox"
            checked={isChecked}
            readOnly
            style={{ accentColor: '#4D148C', cursor: 'pointer' }}
          />
        )}
        <DealLink deal={shipment} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {shipment.dealname || 'Unnamed Deal'}
        </div>
        <span style={{
          background: isWarehouse ? '#3BB273' : isDropOff ? 'var(--color-warning)' : 'var(--color-info)',
          color: isDropOff && !isWarehouse ? '#000' : '#fff',
          fontSize: 9,
          padding: '3px 8px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          fontWeight: 600,
          lineHeight: 1,
          textAlign: 'center'
        }}>
          {isWarehouse ? 'IN PERSON' : isDropOff ? 'DROP-OFF' : 'COURIER'}
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {shipment.contact_name || shipment.seller_email || '-'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* On-Hold (spec 20260911): flagged so staff never book it by accident. */}
        {shipment.collection_on_hold && (
          <span className="badge badge-warning" title={shipment.collection_hold_reason || undefined} style={{ fontSize: 10 }}>
            ⏸ On hold
          </span>
        )}
        {isWarehouse ? (
          <span className="badge" style={{ background: '#3BB273', color: 'white', fontSize: 10 }}>
            {shipment.collection_date || 'No date'} · {slotText}
          </span>
        ) : (
        <span className="badge" style={{ background: carrier.color, color: 'white', fontSize: 10 }}>
          {carrier.name}
        </span>
        )}
        {/* Printer / boxes / auto-assign reason are courier concerns; they
            survive a send-type switch on the booking row but must not render
            on an in-person visit (operator catch 2026-08-06). */}
        {!isWarehouse && hasPrinter && (
          <span className="badge" style={{ fontSize: 9 }}>Printer</span>
        )}
        {!isWarehouse && (
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {boxCount} box · {formatNumber(totalWeight)}kg
        </span>
        )}
      </div>
      {!isWarehouse && (
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
        {autoAssigned.reason}
      </div>
      )}
      {shipment.collection_date && (
        <div style={{ fontSize: 11, color: 'var(--color-info)', marginTop: 4, fontWeight: 500 }}>
          📅 {shipment.collection_date}
        </div>
      )}
    </div>
  )
}
